/**
 * Maquettes SVG (illustrations « capture d'écran ») du Centre d'aide.
 *
 * Schématiques mais aux couleurs de PexiTalk — fiables et nettes à toute taille,
 * sans dépendre d'un backend. Pour remplacer une maquette par une VRAIE capture :
 * déposer le PNG dans `electron-app/public/` et utiliser
 * `{ type: 'figure', src: '/aide-captures/xxx.png', alt: '…', caption: '…' }`.
 *
 * Palette : surface #fff · muted #F1F5F9 · bord #E5E7EB · texte #0F172A /
 * #475569 / #94A3B8 · primaire #7C3AED · succès #10B981 · alerte #F59E0B ·
 * danger #EF4444 · cyan(en appel) #06B6D4.
 */

const F = 'font-family="Inter, system-ui, sans-serif"'

// ── Sélecteur de statut ────────────────────────────────────────────────────
export const FIG_STATUS = `
<svg viewBox="0 0 320 318" xmlns="http://www.w3.org/2000/svg">
  <rect width="320" height="318" fill="#F8FAFC"/>
  <rect x="20" y="18" width="280" height="282" rx="12" fill="#fff" stroke="#E5E7EB"/>
  <text x="36" y="44" ${F} font-size="13" font-weight="700" fill="#0F172A">Changer mon statut</text>
  ${[
    ['#10B981', 'Disponible', true],
    ['#EF4444', 'Indisponible', false],
    ['#F59E0B', 'Pause', false],
    ['#F97316', 'Déjeuner', false],
    ['#14B8A6', 'Réunion', false],
    ['#9CA3AF', 'Hors ligne', false],
  ].map((r, i) => {
    const y = 60 + i * 38
    const sel = r[2]
    return `
    <rect x="32" y="${y}" width="256" height="32" rx="8" fill="${sel ? '#EDE9FE' : 'transparent'}" stroke="${sel ? '#C4B5FD' : 'transparent'}"/>
    <circle cx="50" cy="${y + 16}" r="5" fill="${r[0]}"/>
    <text x="66" y="${y + 20}" ${F} font-size="12.5" font-weight="${sel ? 700 : 500}" fill="${sel ? '#5B21B6' : '#475569'}">${r[1]}</text>
    ${sel ? `<path d="M270 ${y + 12} l4 4 8 -9" fill="none" stroke="#7C3AED" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>` : ''}`
  }).join('')}
</svg>`

// ── Softphone (clavier) ──────────────────────────────────────────────────────
export const FIG_SOFTPHONE = `
<svg viewBox="0 0 300 388" xmlns="http://www.w3.org/2000/svg">
  <rect width="300" height="388" fill="#F8FAFC"/>
  <rect x="20" y="16" width="260" height="356" rx="14" fill="#fff" stroke="#E5E7EB"/>
  <text x="36" y="40" ${F} font-size="11" font-weight="700" letter-spacing="0.5" fill="#94A3B8">TÉLÉPHONE</text>
  <circle cx="250" cy="36" r="4" fill="#10B981"/><text x="232" y="40" ${F} font-size="10" fill="#10B981" text-anchor="end">Prêt</text>
  <rect x="36" y="52" width="228" height="40" rx="9" fill="#EDE9FE"/>
  <text x="50" y="77" ${F} font-size="14" font-weight="600" fill="#5B21B6">+1 514 555 0142</text>
  ${[0,1,2,3].map(rw => [0,1,2].map(c => {
    const labels = [['1','2','3'],['4','5','6'],['7','8','9'],['*','0','#']]
    const x = 36 + c * 78, y = 104 + rw * 52
    return `<rect x="${x}" y="${y}" width="68" height="44" rx="9" fill="#F1F5F9" stroke="#E5E7EB"/>
      <text x="${x + 34}" y="${y + 29}" ${F} font-size="16" font-weight="600" fill="#0F172A" text-anchor="middle">${labels[rw][c]}</text>`
  }).join('')).join('')}
  <rect x="36" y="316" width="228" height="44" rx="22" fill="#10B981"/>
  <text x="150" y="343" ${F} font-size="14" font-weight="700" fill="#fff" text-anchor="middle">Appeler</text>
</svg>`

// ── Fiche de fin d'appel (dispositions) ──────────────────────────────────────
export const FIG_DISPOSITION = `
<svg viewBox="0 0 560 250" xmlns="http://www.w3.org/2000/svg">
  <rect width="560" height="250" fill="#F8FAFC"/>
  <rect x="20" y="18" width="520" height="214" rx="12" fill="#fff" stroke="#E5E7EB"/>
  <text x="40" y="48" ${F} font-size="15" font-weight="700" fill="#0F172A">Comment s'est passé l'appel&nbsp;?</text>
  <text x="40" y="70" ${F} font-size="12" fill="#94A3B8">Choisissez un résultat pour clôturer</text>
  ${[
    ['#3B82F6','Intéressé',40],['#22C55E','Vente',172],['#10B981','RDV',280],
    ['#F59E0B','Pas intéressé',388],
    ['#9CA3AF','Pas de réponse',40,2],['#0D9488','Messagerie',196,2],['#6366F1','Suite à donner',344,2],
  ].map((c) => {
    const row = (c[3] as number) || 1
    const y = 88 + (row - 1) * 46
    const x = c[2] as number
    const w = String(c[1]).length * 7 + 40
    return `<rect x="${x}" y="${y}" width="${w}" height="34" rx="17" fill="#fff" stroke="${c[0]}"/>
      <circle cx="${x + 18}" cy="${y + 17}" r="5" fill="${c[0]}"/>
      <text x="${x + 30}" y="${y + 22}" ${F} font-size="12.5" font-weight="600" fill="#334155">${c[1]}</text>`
  }).join('')}
  <rect x="40" y="188" width="480" height="30" rx="8" fill="#F1F5F9"/>
  <text x="54" y="207" ${F} font-size="11.5" fill="#94A3B8">+ Notes, rappel, formulaire RDV…</text>
</svg>`

// ── Mode d'appel d'une campagne (Aperçu / Progressif) ────────────────────────
export const FIG_CAMPAIGN_MODE = `
<svg viewBox="0 0 560 210" xmlns="http://www.w3.org/2000/svg">
  <rect width="560" height="210" fill="#F8FAFC"/>
  <rect x="20" y="18" width="520" height="174" rx="12" fill="#fff" stroke="#E5E7EB"/>
  <text x="40" y="46" ${F} font-size="12" font-weight="700" fill="#475569">Mode d'appel</text>
  <rect x="40" y="58" width="230" height="48" rx="10" fill="#fff" stroke="#C4B5FD"/>
  <circle cx="64" cy="82" r="7" fill="#fff" stroke="#CBD5E1"/>
  <text x="82" y="79" ${F} font-size="13" font-weight="700" fill="#0F172A">Aperçu</text>
  <text x="82" y="96" ${F} font-size="10.5" fill="#94A3B8">L'agent valide avant l'appel</text>
  <rect x="290" y="58" width="230" height="48" rx="10" fill="#EDE9FE" stroke="#7C3AED"/>
  <circle cx="314" cy="82" r="7" fill="#7C3AED"/><circle cx="314" cy="82" r="3" fill="#fff"/>
  <text x="332" y="79" ${F} font-size="13" font-weight="700" fill="#5B21B6">Progressif</text>
  <text x="332" y="96" ${F} font-size="10.5" fill="#7C3AED">Le système compose tout seul</text>
  <rect x="40" y="124" width="480" height="48" rx="10" fill="#F0FDF4" stroke="#A7F3D0"/>
  <text x="58" y="146" ${F} font-size="11.5" font-weight="700" fill="#065F46">✓ L'agent ne reçoit que les vraies personnes</text>
  <text x="58" y="163" ${F} font-size="10.5" fill="#047857">Les répondeurs sont écartés automatiquement.</text>
</svg>`

// ── Page Rappels ─────────────────────────────────────────────────────────────
export const FIG_CALLBACKS = `
<svg viewBox="0 0 560 236" xmlns="http://www.w3.org/2000/svg">
  <rect width="560" height="236" fill="#F8FAFC"/>
  <text x="20" y="30" ${F} font-size="15" font-weight="800" fill="#0F172A">Rappels</text>
  <rect x="20" y="44" width="86" height="24" rx="12" fill="#FEE2E2"/><text x="63" y="60" ${F} font-size="11" font-weight="700" fill="#B91C1C" text-anchor="middle">En retard 2</text>
  <rect x="114" y="44" width="86" height="24" rx="12" fill="#F1F5F9"/><text x="157" y="60" ${F} font-size="11" font-weight="600" fill="#475569" text-anchor="middle">Aujourd'hui 5</text>
  <rect x="208" y="44" width="80" height="24" rx="12" fill="#F1F5F9"/><text x="248" y="60" ${F} font-size="11" font-weight="600" fill="#475569" text-anchor="middle">À venir 8</text>
  ${[
    ['09:30','Marie Dupont','+1 514 555 0142','#EF4444','En retard'],
    ['14:00','Jean Martin','+1 438 555 7781','#F59E0B','Bientôt'],
    ['16:15','Sophie Bernard','+1 514 555 9920','#94A3B8','À venir'],
  ].map((r, i) => {
    const y = 84 + i * 46
    return `<rect x="20" y="${y}" width="520" height="38" rx="9" fill="#fff" stroke="#E5E7EB"/>
      <text x="36" y="${y + 24}" ${F} font-size="12.5" font-weight="700" fill="#5B21B6">${r[0]}</text>
      <text x="92" y="${y + 24}" ${F} font-size="12.5" font-weight="600" fill="#0F172A">${r[1]}</text>
      <text x="250" y="${y + 24}" ${F} font-size="12" fill="#64748B">${r[2]}</text>
      <circle cx="404" cy="${y + 19}" r="4" fill="${r[3]}"/><text x="416" y="${y + 23}" ${F} font-size="11" fill="${r[3]}">${r[4]}</text>
      <rect x="470" y="${y + 8}" width="54" height="22" rx="11" fill="#EDE9FE"/><text x="497" y="${y + 23}" ${F} font-size="10.5" font-weight="700" fill="#5B21B6" text-anchor="middle">Appeler</text>`
  }).join('')}
</svg>`

// ── Liste noire (action en appel) ────────────────────────────────────────────
export const FIG_BLACKLIST = `
<svg viewBox="0 0 480 196" xmlns="http://www.w3.org/2000/svg">
  <rect width="480" height="196" fill="#F8FAFC"/>
  <rect x="20" y="18" width="440" height="160" rx="12" fill="#fff" stroke="#E5E7EB"/>
  <circle cx="56" cy="58" r="16" fill="#FEE2E2"/>
  <path d="M48 58 h16 M56 50 v16" fill="none" stroke="#EF4444" stroke-width="2.5" stroke-linecap="round" transform="rotate(45 56 58)"/>
  <text x="84" y="54" ${F} font-size="14" font-weight="700" fill="#0F172A">Mettre en liste noire</text>
  <text x="84" y="74" ${F} font-size="12" fill="#64748B">+1 514 555 0142 — ne sera plus jamais appelé</text>
  <rect x="40" y="98" width="400" height="34" rx="8" fill="#F1F5F9"/>
  <text x="54" y="120" ${F} font-size="11.5" fill="#94A3B8">Motif (optionnel) : demande du contact</text>
  <rect x="280" y="142" width="76" height="30" rx="8" fill="#fff" stroke="#E5E7EB"/><text x="318" y="161" ${F} font-size="12" fill="#475569" text-anchor="middle">Annuler</text>
  <rect x="364" y="142" width="96" height="30" rx="8" fill="#EF4444"/><text x="412" y="161" ${F} font-size="12" font-weight="700" fill="#fff" text-anchor="middle">Confirmer</text>
</svg>`
